import { displayError, getApiUrl, getCurrentLanguageId, addEventListenerIfNotExists } from "./common";
import HttpClient from "./http-client";
import LoadingIndication from "./loading-indication";
import { loadStripe } from '@stripe/stripe-js/pure';

// Response body of a session request the gateway refused because the order total is below its minimum.
const AMOUNT_BELOW_MINIMUM = 'payment_amount_below_minimum';

interface StripeSession {
    SessionId: string;
    PublicKey: string;
}

export default class StripePayment {
    private readonly baseElement: HTMLElement;
    private readonly buttonElement: HTMLElement;
    private readonly orderGuid: string;

    private static _componentName = 'StripePayment';
    private static buttonName = 'stripe-proceed-button';

    static get componentName() {
        return StripePayment._componentName;
    }

    constructor() {
        this.baseElement = document.querySelector(`[data-component="${StripePayment.componentName}"]`);
        this.orderGuid = this.baseElement.dataset.orderGuid;
        this.buttonElement = this.baseElement.querySelector(`[data-component-part="${StripePayment.buttonName}"]`);
        addEventListenerIfNotExists(this.buttonElement, 'click', (evt) => this.procceed(evt));
    }

    private async procceed(evt: Event): Promise<void> {
        evt.preventDefault();
        this.startLoading();
        const session = await this.createSession();
        if (!session) return;

        await this.redirectToPayment(session);
    }

    private async redirectToPayment(session: StripeSession): Promise<void> {
        const stripe = await loadStripe(session.PublicKey);
        const result = await stripe.redirectToCheckout({ sessionId: session.SessionId });
        this.stopLoading();
        if (result.error.message) {
            displayError(result.error.message, "Connection to payment gateway failed.");
        }
    }

    private async createSession(): Promise<StripeSession> {
        const url = `${getApiUrl()}/stripepayment/session`;
        const response = await new HttpClient().fetch(url, {
            method: 'POST',
            body: JSON.stringify({ 
                orderGuid: this.orderGuid,
                returnUrl: window.location.href,
            }),
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        }, (e) => {
            // An error response is handled below, where its body can be read.
            if (e instanceof Response) return;
            this.stopLoading();
            displayError("Payment session initialization failed. " + e);
        });

        if (!response) return null;

        if (!response.ok) {
            this.stopLoading();
            const belowMinimumMessage = this.baseElement.dataset.amountBelowMinimumMessage;
            if (belowMinimumMessage && await this.isAmountBelowMinimum(response)) {
                displayError(belowMinimumMessage);
                return null;
            }
            displayError("Payment session initialization failed.");
            return null;
        }

        const json = await response.json();
        const session = JSON.parse(json) as StripeSession;
        if (!session.PublicKey || !session.SessionId) {
            this.stopLoading();
            displayError("Payment session initialization failed.");
            return null;
        }

        return session;
    }

    private async isAmountBelowMinimum(response: Response): Promise<boolean> {
        try {
            return (await response.json()) === AMOUNT_BELOW_MINIMUM;
        } catch {
            return false;
        }
    }

    private startLoading() {
        LoadingIndication.show(this.baseElement);
    }

    private stopLoading() {
        LoadingIndication.hide(this.baseElement);
    }
}
